import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Box, Button, Skeleton } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import mermaid from 'mermaid'

interface MermaidDiagramProps {
	config: Record<string, any> | null
	graphDefinition: string | null
	categories?: Array<{ id: number; name: string }>
	feedbackId?: number
	editAllowed?: boolean
	onEditClick?: () => void
	translations: Record<string, string>
}

/**
 * Helper to calculate estimated height for skeleton
 */
const calculateSkeletonHeight = (
	graphDefinition: string | null,
	categories?: Array<{ id: number; name: string }>,
): number => {
	if (!graphDefinition) return 40

	// Use graphDefinition to find leaf nodes for more accuracy
	const lines = graphDefinition.split('\n')
	const nodes = lines.filter((line) => line.includes('["'))
	const links = lines.filter((line) => line.includes(' --> '))

	// Find nodes that are not parents in any link
	const parentNodeIds = new Set(
		links.map((link) => {
			const parts = link.split(' --> ')
			return parts[0].trim()
		}),
	)
	const leafNodes = nodes.filter((node) => {
		const nodeId = node.trim().split('[')[0].trim()
		return !parentNodeIds.has(nodeId)
	})

	const numLeafNodes = Math.max(1, leafNodes.length)

	// Height calculation based on leaf nodes
	let totalHeight = 0
	leafNodes.forEach((node) => {
		// Extract category name from label
		const nameMatch = node.match(/<span>(.*?)<\/span>/)
		const name = nameMatch ? nameMatch[1] : ''

		// We use leaf nodes because in LR diagram, vertical height is determined by number of terminal nodes
		const numLines = Math.ceil(name.length / 18) || 1
		let nodeHeight = 36
		if (numLines === 2) nodeHeight = 50
		else if (numLines >= 3) nodeHeight = 80 // Approx for 3+ lines

		totalHeight += nodeHeight
	})

	// Add space between nodes (Mermaid default nodeSpacing is 10)
	totalHeight += (numLeafNodes - 1) * 12

	// Add small padding for SVG container
	totalHeight += 16

	return Math.max(40, totalHeight)
}

/**
 * Component to render a structured skeleton that looks like a mermaid graph
 */
const MermaidSkeleton: React.FC<{
	height: number
	categories?: Array<{ id: number; name: string }>
	graphDefinition: string | null
}> = ({ height, categories, graphDefinition }) => {
	// Identify leaf nodes from graphDefinition
	const leafNodesInfo = useMemo(() => {
		if (!graphDefinition) return []

		const lines = graphDefinition.split('\n')
		const nodesInGraph = lines.filter((line) => line.includes('["'))
		const links = lines.filter((line) => line.includes(' --> '))

		// Correctly identify parent node IDs by splitting at the first " --> "
		const parentNodeIds = new Set(
			links.map((link) => {
				const parts = link.split(' --> ')
				return parts[0].trim()
			}),
		)

		return nodesInGraph
			.filter((node) => {
				const nodeId = node.trim().split('[')[0].trim()
				return !parentNodeIds.has(nodeId)
			})
			.map((node) => {
				const idMatch = node.match(/C(\d+)/)
				const id = idMatch ? parseInt(idMatch[1], 10) : null
				const nameMatch = node.match(/<span>(.*?)<\/span>/)
				const name = nameMatch ? nameMatch[1] : ''

				// Find parent name if it exists to show "Parent > Child" in skeleton?
				// Actually the user wants 1 bigger skeleton for 1 child category if it has parent.
				// But let's just make sure we only render one row per leaf.
				return { id, name, nodeLine: node }
			})
	}, [graphDefinition])

	const displayRows = leafNodesInfo.length > 0 ? leafNodesInfo : [{ id: null, name: '', nodeLine: '' }]

	return (
		<Box
			sx={{
				width: '100%',
				height,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				gap: '12px',
				p: 1,
				boxSizing: 'border-box',
			}}
		>
			{displayRows.map((row, i) => {
				// Estimate width based on name length
				const nameLength = row.name.length
				const numLines = Math.ceil(nameLength / 18) || 1
				const skeletonHeight = numLines === 1 ? 24 : numLines === 2 ? 40 : 64

				return (
					<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Skeleton
							variant="circular"
							width={14}
							height={14}
							animation="wave"
							sx={{ flexShrink: 0, borderRadius: '4px' }}
						/>
						<Skeleton
							variant="text"
							width={row.name ? `${Math.min(95, 30 + nameLength * 2)}%` : '60%'}
							height={skeletonHeight}
							animation="wave"
						/>
					</Box>
				)
			})}
		</Box>
	)
}

/**
 * Render mermaid diagram for TA categories hierarchy
 * Matches the old grid's MermaidDiagramControl display
 */
export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
	config,
	graphDefinition,
	categories,
	feedbackId,
	editAllowed = false,
	onEditClick,
	translations = {},
}) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const mermaidIdRef = useRef(`mermaid-${Math.random().toString(36).substr(2, 9)}`)
	const [isRendering, setIsRendering] = useState(false)
	const [isRendered, setIsRendered] = useState(false)

	// Reset state when graph changes
	useEffect(() => {
		setIsRendered(false)
		setIsRendering(false)
	}, [graphDefinition])

	useEffect(() => {
		if (!config || !graphDefinition || !containerRef.current || isRendered) {
			return
		}

		setIsRendering(true)

		// Defer rendering to prevent UI lag
		const timeoutId = setTimeout(async () => {
			try {
				// Initialize mermaid with config
				mermaid.initialize({
					startOnLoad: false,
					...config,
				})

				if (!containerRef.current) return

				const { svg } = await mermaid.render(mermaidIdRef.current, graphDefinition)

				if (containerRef.current) {
					containerRef.current.innerHTML = svg
					setIsRendered(true)
				}
			} catch (error) {
				console.error('Failed to render mermaid diagram:', error)
				if (containerRef.current) {
					containerRef.current.innerHTML = `<div>${translations.failedToRenderDiagram || 'Failed to render diagram'}</div>`
				}
			} finally {
				setIsRendering(false)
			}
		}, 0)

		return () => clearTimeout(timeoutId)
	}, [config, graphDefinition, isRendered])

	const skeletonHeight = useMemo(
		() => calculateSkeletonHeight(graphDefinition, categories),
		[graphDefinition, categories],
	)

	return (
		<Box
			sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', minHeight: editAllowed ? '40px' : '0' }}
		>
			{editAllowed && onEditClick && (
				<Button
					variant="contained"
					className="!bg-primary"
					size="small"
					startIcon={<EditIcon />}
					onClick={(e) => {
						e.stopPropagation() // Prevent row click
						onEditClick()
					}}
					sx={{
						alignSelf: 'flex-start',
						px: 2,
						py: 0.5,
						borderRadius: '12px',
						boxShadow: 1,
						'&:hover': {
							boxShadow: 2,
						},
					}}
				>
					{translations.edit || 'Edit'}
				</Button>
			)}

			{graphDefinition ? (
				<Box sx={{ width: '100%', position: 'relative' }}>
					{(!isRendered || isRendering) && (
						<MermaidSkeleton height={skeletonHeight} categories={categories} graphDefinition={graphDefinition} />
					)}
					<Box
						ref={containerRef}
						sx={{
							width: '100%',
							overflow: 'auto',
							display: isRendered ? 'block' : 'none',
							'& svg': {
								maxWidth: '100%',
								height: 'auto',
							},
						}}
					/>
				</Box>
			) : (
				!editAllowed && null
			)}
		</Box>
	)
}
